// ── The public Our Work page, arranged ─────────────────────────────────────
//
// Julian photographs finished jobs and wants them shown as named portfolios:
// decks in one place, basements in another, so he can send a link to the
// collection that matches what a caller is asking about.
//
// One record per completed job carries the NAME of the portfolio it belongs
// to. There is no second table of portfolios, because a portfolio with no
// projects in it is nothing a visitor should ever see, and a name typed on
// the project is a name that cannot drift out of step with one stored apart
// from it.
//
// Three rules this file exists to hold:
//
//   1. Portfolios keep an order Julian controls. Each project has a
//      position; a portfolio sits where its best-placed project sits. To
//      move "Basements" to the top he moves one basement to the top, which
//      is the same gesture he already uses to arrange the photos themselves.
//
//   2. The name is matched loosely and printed exactly. "decks" and "Decks"
//      are one portfolio, and the spelling shown is the one on the project
//      Julian placed first, so fixing capitalisation anywhere fixes it
//      everywhere.
//
//   3. A project with no portfolio still appears. It lands in a group with
//      an empty name that the page prints under a heading of its own, rather
//      than vanishing because of a field nobody filled in.

// What a portfolio name becomes in a URL: qpshub.com/work/decks-patios.
// Lowercase, letters and digits kept, everything else a single hyphen. An
// ampersand is dropped rather than spelled out, so "Decks & Patios" and
// "Decks and Patios" do not become two different addresses.
export function slugOf(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/&/g, ' ')
    .replace(/\band\b/g, ' ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// The cover photo for a project: the one marked as cover, else the first.
// A project whose photos were all removed has no cover and the page draws
// its empty panel, the same as a job photo slot that was never filled.
export function coverOf(project) {
  const photos = Array.isArray(project && project.photos) ? project.photos : [];
  const marked = photos.find((p) => p && p.isCover && p.url);
  const first = photos.find((p) => p && p.url);
  return (marked || first || null);
}

// Sort key for a project. `position` is what Julian sets; two projects left
// at the same position fall back to the newest finished job first, so a page
// he has never arranged still reads sensibly instead of in insert order.
function orderOf(p) {
  const n = Number(p && p.position);
  return Number.isFinite(n) ? n : 0;
}
function finishedAt(p) {
  const t = p && p.completedOn ? Date.parse(p.completedOn) : NaN;
  return Number.isFinite(t) ? t : 0;
}
function compare(a, b) {
  const byHand = orderOf(a) - orderOf(b);
  if (byHand !== 0) return byHand;
  return finishedAt(b) - finishedAt(a);
}

// Group projects into the portfolios the page draws.
//
// Returns [{ name, slug, projects }], portfolios in Julian's order, the
// projects inside each in his order too. Nothing is invented: a portfolio
// exists here only because a project named it.
export function groupProjects(projects) {
  const list = (Array.isArray(projects) ? projects : []).filter(Boolean);
  const byKey = new Map();

  // Placed in order first, so "the project Julian placed first" is a fact
  // about his arrangement rather than about which row the database returned
  // first. Both the printed spelling and the group's position come from it.
  const sorted = [...list].sort(compare);

  for (const p of sorted) {
    const raw = typeof p.portfolio === 'string' ? p.portfolio.trim() : '';
    const key = slugOf(raw);
    if (!byKey.has(key)) byKey.set(key, { name: raw, slug: key, projects: [] });
    byKey.get(key).projects.push(p);
  }

  // Map preserves insertion order, and insertion followed the sort, so the
  // groups are already in Julian's order. An unnamed group goes last however
  // it was placed: "the rest" reading above a named portfolio looks like a
  // mistake on the page.
  const groups = [...byKey.values()];
  const named = groups.filter((g) => g.slug);
  const rest = groups.filter((g) => !g.slug);
  return [...named, ...rest];
}

// Everything the page needs in one pass: the groups, and the flat list in the
// same order for the "All work" view. Built from the same sort, so a project
// cannot sit third under its portfolio and first under All.
export function arrangeWork(projects) {
  const groups = groupProjects(projects);
  const all = groups.reduce((acc, g) => acc.concat(g.projects), []);
  return { groups, all };
}

// Which portfolio a URL slug selects, or null for "show everything".
//
// The lookup cannot be a plain find(). Projects with no portfolio name are
// grouped under an EMPTY slug, and an empty filter means "all work" — so a
// bare find() matches that group and /work opens showing the handful of
// loose projects instead of the whole page. An unnamed group is reachable
// only by browsing, never by address, which is the right answer anyway:
// there is no sensible URL for "the ones with no name".
export function pickGroup(groups, slug) {
  if (!slug) return null;

  const list = Array.isArray(groups) ? groups : [];
  return list.find((g) => g && g.slug === slug) || null;
}
